import React, {
  useEffect,
  useState,
} from "react";

import {
  FiAlertTriangle,
  FiPackage,
  FiRotateCcw,
  FiX,
} from "react-icons/fi";


/* =========================================================
   COMPONENT
========================================================= */

const ReturnOrderDialog = ({
  open,

  order,

  loading = false,

  onClose,

  onSubmit,
}) => {
  /* =======================================================
     FORM
  ======================================================= */

  const [
    reason,
    setReason,
  ] = useState("");


  const [
    note,
    setNote,
  ] = useState("");


  /* =======================================================
     ERROR
  ======================================================= */

  const [
    error,
    setError,
  ] = useState("");


  /* =======================================================
     RESET
  ======================================================= */

  useEffect(() => {
    if (!open) {
      return;
    }

    setReason("");

    setNote("");

    setError("");

  }, [
    open,
    order?._id,
  ]);


  /* =======================================================
     DO NOT RENDER
  ======================================================= */

  if (!open) {
    return null;
  }


  /* =======================================================
     CLOSE
  ======================================================= */

  const handleClose =
    () => {
      if (loading) {
        return;
      }

      if (
        typeof onClose ===
        "function"
      ) {
        onClose();
      }
    };


  /* =======================================================
     SUBMIT
  ======================================================= */

  const handleSubmit =
    (
      event
    ) => {
      event.preventDefault();


      if (loading) {
        return;
      }


      const cleanReason =
        String(
          reason || ""
        ).trim();


      if (!cleanReason) {
        setError(
          "Return reason is required."
        );

        return;
      }


      if (
        typeof onSubmit ===
        "function"
      ) {
        onSubmit({
          reason:
            cleanReason,

          note:
            String(
              note || ""
            ).trim(),
        });
      }
    };


  /* =======================================================
     DISPLAY DATA
  ======================================================= */

  const orderNumber =
    order?.orderNumber ||
    "-";


  const customerName =
    order
      ?.customer
      ?.fullName ||
    order
      ?.userId
      ?.name ||
    order
      ?.user
      ?.name ||
    "-";


  const mobile =
    order
      ?.customer
      ?.mobile ||
    order
      ?.userId
      ?.mobile ||
    order
      ?.user
      ?.mobile ||
    "-";


  /* =======================================================
     RENDER
  ======================================================= */

  return (
    <div
      className="order-dialog-overlay"
    >
      {/* =================================================
          BACKDROP
      ================================================= */}

      <button
        type="button"
        className="order-dialog-backdrop"
        onClick={
          loading
            ? undefined
            : handleClose
        }
        aria-label="Close return order dialog"
      />


      {/* =================================================
          DIALOG
      ================================================= */}

      <div
        className="order-dialog order-return-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="return-order-title"
      >
        {/* ===============================================
            HEADER
        =============================================== */}

        <div
          className="order-dialog-header"
        >
          <div
            className="order-dialog-header-icon return"
          >
            <FiRotateCcw />
          </div>


          <div
            className="order-dialog-header-content"
          >
            <span>
              CUSTOMER RETURN
            </span>

            <h3
              id="return-order-title"
            >
              Return Order
            </h3>

            <p>
              {orderNumber}
            </p>
          </div>


          <button
            type="button"
            className="order-dialog-close"
            onClick={
              handleClose
            }
            disabled={
              loading
            }
            aria-label="Close"
          >
            <FiX />
          </button>
        </div>


        {/* ===============================================
            ORDER SUMMARY
        =============================================== */}

        <div
          className="order-dialog-order-summary"
        >
          <div>
            <FiPackage />

            <span>
              Order
            </span>

            <strong>
              {orderNumber}
            </strong>
          </div>


          <div>
            <FiRotateCcw />

            <span>
              Status
            </span>

            <strong>
              {String(
                order?.status ||
                "-"
              ).replace(
                /_/g,
                " "
              )}
            </strong>
          </div>
        </div>


        {/* ===============================================
            FORM
        =============================================== */}

        <form
          onSubmit={
            handleSubmit
          }
        >
          <div
            className="order-dialog-body"
          >
            {/* ===========================================
                INFO
            =========================================== */}

            <div
              className="order-dialog-info return"
            >
              <FiPackage />

              <div>
                <strong>
                  Delivered order return
                </strong>

                <span>
                  This action will mark the order as
                  returned and restore returned quantity
                  to inventory.
                </span>
              </div>
            </div>


            {/* ===========================================
                CUSTOMER
            =========================================== */}

            <div
              className="order-return-customer-summary"
            >
              <div>
                <span>
                  Customer
                </span>

                <strong>
                  {customerName}
                </strong>
              </div>


              <div>
                <span>
                  Mobile
                </span>

                <strong>
                  {mobile}
                </strong>
              </div>


              <div>
                <span>
                  Payment Status
                </span>

                <strong>
                  {String(
                    order
                      ?.paymentStatus ||
                    "-"
                  ).replace(
                    /_/g,
                    " "
                  )}
                </strong>
              </div>


              <div>
                <span>
                  Payment Method
                </span>

                <strong>
                  {order
                    ?.paymentMethod ===
                  "cod"
                    ? "Cash on Delivery"
                    : "Online"}
                </strong>
              </div>
            </div>


            {/* ===========================================
                RETURN REASON
            =========================================== */}

            <div
              className="order-dialog-field"
            >
              <label>
                Return Reason *
              </label>

              <textarea
                rows={4}
                value={
                  reason
                }
                onChange={(
                  event
                ) => {
                  setReason(
                    event.target
                      .value
                  );

                  setError("");
                }}
                placeholder="Enter customer return reason..."
                disabled={
                  loading
                }
                autoFocus
              />

              {error ? (
                <small>
                  {error}
                </small>
              ) : null}
            </div>


            {/* ===========================================
                INTERNAL NOTE
            =========================================== */}

            <div
              className="order-dialog-field"
            >
              <label>
                Internal Note
              </label>

              <textarea
                rows={3}
                value={
                  note
                }
                onChange={(
                  event
                ) =>
                  setNote(
                    event.target
                      .value
                  )
                }
                placeholder="Optional note about the returned product..."
                disabled={
                  loading
                }
              />
            </div>


            {/* ===========================================
                STOCK WARNING
            =========================================== */}

            <div
              className="order-return-stock-note"
            >
              <FiAlertTriangle />

              <div>
                <strong>
                  Inventory will be updated
                </strong>

                <span>
                  The backend will restore returned
                  quantity and create a customer return
                  stock movement.
                </span>
              </div>
            </div>


            {/* ===========================================
                REFUND WARNING
            =========================================== */}

            {[
              "paid",
              "partially_refunded",
            ].includes(
              order
                ?.paymentStatus
            ) ? (
              <div
                className="order-dialog-refund-warning"
              >
                <FiAlertTriangle />

                <div>
                  <strong>
                    Payment refund may be required
                  </strong>

                  <span>
                    Returning the order updates inventory.
                    Payment refund should be handled
                    separately in the payment/refund flow.
                  </span>
                </div>
              </div>
            ) : null}
          </div>


          {/* ===============================================
              FOOTER
          =============================================== */}

          <div
            className="order-dialog-footer"
          >
            <button
              type="button"
              className="secondary"
              onClick={
                handleClose
              }
              disabled={
                loading
              }
            >
              Cancel
            </button>


            <button
              type="submit"
              className="return"
              disabled={
                loading
              }
            >
              {loading ? (
                <>
                  <span
                    className="order-button-spinner"
                  />

                  Returning...
                </>
              ) : (
                <>
                  <FiRotateCcw />

                  Mark Returned
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};


export default
  ReturnOrderDialog;